import { useCallback, useMemo } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'

export const FILTER_PARAM = 'filter'
export const FILTERS_TOUCHED_STATE_KEY = 'advancedFiltersTouched'

export function encodeAdvancedFilters(filters) {
    if (!filters || filters.length === 0) return null
    try {
        return btoa(unescape(encodeURIComponent(JSON.stringify(filters))))
    } catch (e) {
        return null
    }
}

export function decodeAdvancedFilters(param) {
    if (!param) return []
    try {
        const parsed = JSON.parse(decodeURIComponent(escape(atob(param))))
        return Array.isArray(parsed) ? parsed : []
    } catch (e) {
        return []
    }
}

// The `?filter=` query param is the single source of truth for advanced
// filters -- any component can call this hook to read the current filters or
// change them, with no prop drilling. Every consumer calling useSearchParams()
// re-renders when the URL changes, regardless of which component wrote it, so
// e.g. the popover applying a filter and a chip removing one both just work.
export function useAdvancedFilters() {
    const [searchParams] = useSearchParams()
    const location = useLocation()
    const navigate = useNavigate()
    const filters = useMemo(() => decodeAdvancedFilters(searchParams.get(FILTER_PARAM)), [searchParams])

    // Deliberately not useSearchParams()'s own setter: it always calls
    // navigate("?" + params, …) - a bare search string with no hash - and
    // react-router's resolveTo() defaults a missing hash to "" (see
    // @remix-run/router's resolvePath), which silently drops the tab from the
    // URL on any page that keeps its active tab in the hash (Material/TabbedListingPage.js).
    // Building the "to" object ourselves and carrying over the current
    // location.hash keeps it intact. This relies on the router's location
    // being authoritative for the hash, which TabbedListingPage's own
    // navigate()-based hash writes guarantee. It does NOT hold for the ~8
    // legacy tabbed containers (e.g. CarReturn/CarReturnListContainer.js) that still write
    // window.location.hash directly on their own routes without going through navigate() -
    // there, the router's location.hash can be stale, so applying a filter on one of those
    // pages carries over a stale hash instead of the (correct) empty one it used to write.
    // Not a regression (both are wrong) and out of scope here to fix properly.
    const setFilters = useCallback((newFilters) => {
        const nextParams = new URLSearchParams(location.search)
        const encoded = encodeAdvancedFilters(newFilters)
        if (encoded) {
            nextParams.set(FILTER_PARAM, encoded)
        } else {
            nextParams.delete(FILTER_PARAM)
        }
        // Marks this history entry as "the filters here were set by the user".
        // A page that persists filters needs to tell a real edit apart from a
        // plain navigation that happens to drop `?filter=` -- every in-app link
        // is built without a search string, so following one looks identical to
        // clearing every filter by hand. Existing state is preserved: other
        // pages put their own things here.
        navigate(
            { pathname: location.pathname, search: nextParams.toString(), hash: location.hash },
            { replace: true, state: { ...(location.state || {}), [FILTERS_TOUCHED_STATE_KEY]: true } }
        )
    }, [navigate, location])

    return [filters, setFilters]
}

// True only on a history entry whose filters were set through setFilters above.
export function advancedFiltersWereTouched(location) {
    return !!location?.state?.[FILTERS_TOUCHED_STATE_KEY]
}
